import {Node} from "@tiptap/core";
import type {AnyExtension} from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import Blockquote from "@tiptap/extension-blockquote";
import Code from "@tiptap/extension-code";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Italic from "@tiptap/extension-italic";
import {BulletList, ListItem, OrderedList} from "@tiptap/extension-list";
import Strike from "@tiptap/extension-strike";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Text from "@tiptap/extension-text";
import TextAlign from "@tiptap/extension-text-align";
import {Color, TextStyle} from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import {PrintoutVariableNode} from "../../variables/PrintoutVariableNode";
import {PRINTOUT_BLOCK_EXTENSIONS} from "../blockRegistry";
import {PrintoutDocumentNode} from "../headerFooter/PrintoutDocumentNode";
import {PRINTOUT_REGION_EXTENSIONS} from "../headerFooter/printoutRegionNodes";
import {PrintoutParagraphNode} from "./PrintoutParagraphNode";

export const PRINTOUT_HEADING_LEVELS: [1, 2, 3, 4] = [1, 2, 3, 4];

export interface PrintoutBaseExtensionOptions {
    editable?: boolean;
}

// Strip node editing behavior from read-only preview mounts.
const toReadOnlyPrintoutExtension = (extension: AnyExtension): AnyExtension => (
    extension instanceof Node
        ? extension.extend({
            addProseMirrorPlugins: () => [],
            draggable: false,
            selectable: false,
        })
        : extension
);

export const createPrintoutBaseExtensions = (
    {editable = true}: PrintoutBaseExtensionOptions = {},
): AnyExtension[] => {
    const extensions: AnyExtension[] = [
        PrintoutDocumentNode,
        PrintoutParagraphNode,
        Text,
        PrintoutVariableNode,
        ...PRINTOUT_REGION_EXTENSIONS,
        ...PRINTOUT_BLOCK_EXTENSIONS,
        TextStyle,
        Color,
        Highlight.configure({multicolor: true}),
        Bold,
        Italic,
        Underline,
        Code,
        Superscript,
        Subscript,
        Strike,
        Heading.configure({levels: PRINTOUT_HEADING_LEVELS}),
        Blockquote,
        TextAlign.configure({
            types: ["heading", "paragraph"],
        }),
        BulletList,
        OrderedList,
        ListItem,
        HardBreak,
    ];

    return editable ? extensions : extensions.map(toReadOnlyPrintoutExtension);
};
